'use client';

import { useCallback, useMemo } from 'react';
import FileUpload from '../../../atoms/FileUpload/FileUpload';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { FileValidationError } from '../../../atoms/FileUpload/FileUpload';
import type { FileUploadField, UploadUrlResponse } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type FileUploadControlProps = {
  field: FileUploadField;
  theme: 'dark' | 'light';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
  getUploadUrl: (file: File) => Promise<UploadUrlResponse>;
  onUploadComplete?: (fileUrl: string, file: File) => void;
  onFileRemove?: (fileId?: string) => void;
};

/**
 * Form control for file upload fields
 * @component
 * @param {FileUploadControlProps} props - Component props
 * @returns {JSX.Element} FileUpload control component
 */
const FileUploadControl = ({
  field,
  theme,
  register,
  setValue,
  watch,
  errors,
  getUploadUrl,
  onUploadComplete,
  onFileRemove,
}: FileUploadControlProps) => {
  const { disabled: formDisabled } = useFormContext();
  const isDisabled = field.disabled || formDisabled;
  const watchedFiles = watch(field.name);
  const currentFiles = useMemo(() => watchedFiles || [], [watchedFiles]);

  const handleValidationError = useCallback(
    (error: FileValidationError): string | undefined => {
      if (error.type === 'FILE_TOO_LARGE' && field.validationMessages?.fileTooLarge) {
        const fileSizeMB = (error.file.size / (1024 * 1024)).toFixed(2);
        const maxSizeMB = error.maxFileSize ? Math.round(error.maxFileSize / (1024 * 1024)) : 10;
        return field.validationMessages.fileTooLarge
          .replace('{{fileSize}}', fileSizeMB)
          .replace('{{maxSize}}', String(maxSizeMB));
      }

      if (error.type === 'INVALID_FILE_TYPE' && field.validationMessages?.invalidFileType) {
        const fileType = `.${error.file.name.split('.').pop()?.toLowerCase()}`;
        return field.validationMessages.invalidFileType.replace('{{fileType}}', fileType);
      }

      return undefined;
    },
    [field.validationMessages]
  );

  // Register the field for form validation
  register(field.name, {
    required: field.required ? field?.validationMessages?.required : false,
    validate: field.required
      ? (value) => {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return field?.validationMessages?.required || 'At least one file is required';
          }
          return true;
        }
      : undefined,
  });

  const handleUploadComplete = useCallback(
    (fileUrl: string, file: File) => {
      const updatedFiles = [...currentFiles, { url: fileUrl, name: file.name }];
      setValue(field.name, updatedFiles, { shouldValidate: true });
      onUploadComplete?.(fileUrl, file);
    },
    [currentFiles, field.name, setValue, onUploadComplete]
  );

  const handleFileRemove = useCallback(
    (fileId?: string) => {
      const updatedFiles = currentFiles.filter(
        (f: { name: string; url: string }) => f.url !== fileId
      );
      setValue(field.name, updatedFiles, { shouldValidate: true });
      // Call parent callback to handle server-side deletion
      onFileRemove?.(fileId);
    },
    [currentFiles, field.name, setValue, onFileRemove]
  );

  return (
    <FileUpload
      dataTestId={`form-field-${field.name}`}
      variant={field.variant}
      theme={theme}
      label={field.label}
      uploadButtonLabel={field.uploadButtonLabel}
      helperText={field.helperText}
      dropzoneText={field.dropzoneText}
      accept={field.accept}
      maxFileSize={field.maxFileSize}
      multiple={field.multiple}
      maxRetries={field.maxRetries}
      timeoutMs={field.timeoutMs}
      required={field.required}
      disabled={isDisabled}
      getUploadUrl={getUploadUrl}
      onUploadComplete={handleUploadComplete}
      onFileRemove={handleFileRemove}
      onValidationError={handleValidationError}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      initialFiles={field.initialFiles}
      onDocumentClick={field.onDocumentClick}
      removable={field.removable}
    />
  );
};

export default FileUploadControl;
